import type { ReactNode } from "react";

import type { BaseInputProps } from "../base-input/base-input";

import { useCallback, useRef, useState } from "react";

import clsx from "clsx";
import { Button, Popover, Select } from "react-aria-components";

import { ArrowDownIcon } from "../../../icons/arrow-down";
import { CloseOutlineFilled } from "../../../icons/close-outline-filled";
import { BaseInput } from "../base-input";

import styles from "./select-base.module.css";

type SelectBaseProps = {
	children: (updateValue: (val: null | string) => void) => ReactNode;
	onChange: (val: null | string) => void;
} & Omit<BaseInputProps, "onChange" | "type">;

export function SelectBase({
	children,
	className,
	description,
	errorMessage,
	isDisabled,
	isInvalid,
	isReadOnly,
	label,
	onChange,
	placeholder,
	slotLeft,
	value,
}: SelectBaseProps): ReactNode {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	// Used for correcly sizing the popup according to the trigger
	const triggerRef = useRef<HTMLDivElement>(null);

	const updateValue = useCallback(
		(value: null | string) => {
			onChange(value);
			setIsOpen(false);
			setTimeout(() => {
				triggerRef.current?.querySelector<HTMLElement>("button")?.focus();
			}, 0);
		},
		[setIsOpen, onChange],
	);

	return (
		<Select className={className}>
			<div className={styles.inputWrapper} ref={triggerRef}>
				<BaseInput
					description={description}
					errorMessage={errorMessage}
					isDisabled={isDisabled}
					isInvalid={isInvalid}
					isReadOnly={isReadOnly}
					label={label}
					onPress={() => {
						return setIsOpen(true);
					}}
					placeholder={placeholder}
					slotLeft={slotLeft}
					type="autocomplete"
					value={value}
				/>
				{!isDisabled &&
					!isReadOnly &&
					(value ? (
						<Button
							className={styles.clearButton}
							data-slot="close-icon"
							onPress={() => {
								return onChange(null);
							}}
							//Slot null so the popover correcly targets the input
							slot={null}
						>
							<CloseOutlineFilled />
						</Button>
					) : (
						<ArrowDownIcon
							className={clsx(styles.icon, isOpen && styles.iconOpen)}
							data-testid="arrow-down-icon"
						/>
					))}
			</div>
			<Popover
				className={styles.popoverRoot}
				isOpen={isOpen}
				onOpenChange={setIsOpen}
				placement="bottom"
				shouldCloseOnInteractOutside={() => {
					return true;
				}}
				triggerRef={triggerRef}
			>
				<div
					className={styles.popover}
					// This is used for targeting the right outline
					// on :focus-visible
					data-popover-container
				>
					{children(updateValue)}
				</div>
			</Popover>
		</Select>
	);
}
